// Timeline, the social feed tab. Other travellers' public moments, newest
// first, from GET /explore/moments. Tapping a row opens the entry, tapping
// an author opens their timeline, and the control at the top opens search.
//
// Search is a button shaped like a field rather than a field: tapping it
// opens the discovery grid, which is a screen of photos, not an empty box
// waiting to be typed into.
import { useEffect, useState } from "react";
import { Icon, IconGlobe, IconRefresh, IconSearch } from "@traveldiary/ui";
import { EmptyState } from "../components/EmptyState";
import { MomentDetail } from "../components/MomentDetail";
import { MomentRow } from "../components/MomentRow";
import { Sheet } from "../components/Sheet";
import { TravellerTimeline } from "../components/TravellerTimeline";
import { navigate } from "../lib/router";
import type { AuthoredMoment, PublicUser } from "../lib/types";
import { useApp } from "../state/app";

type TimelineSheet =
  | { kind: "author"; user: PublicUser }
  | { kind: "entry"; entry: AuthoredMoment };

export function TimelineScreen() {
  const { t, exploreMoments, exploreFailed, refreshExplore, focusOnMap } = useApp();
  const [sheet, setSheet] = useState<TimelineSheet | null>(null);

  useEffect(() => {
    void refreshExplore();
  }, [refreshExplore]);

  function openOnMap(entry: AuthoredMoment): void {
    if (entry.lat === null || entry.lng === null) return;
    focusOnMap({ lat: entry.lat, lng: entry.lng, momentId: entry.id });
    navigate("map");
  }

  return (
    <div className="screen-pad">
      <button type="button" className="search-trigger" onClick={() => navigate("search")}>
        <Icon size={18} color="var(--color-text-muted)">
          <IconSearch />
        </Icon>
        <span className="search-trigger-text">{t("search.placeholder")}</span>
      </button>

      {exploreFailed ? (
        <div className="row-actions">
          <p className="field-note" role="status">
            {t("explore.failed")}
          </p>
          <button type="button" className="btn btn-quiet" onClick={() => void refreshExplore()}>
            <Icon size={18}>
              <IconRefresh />
            </Icon>
            {t("common.retry")}
          </button>
        </div>
      ) : null}

      {exploreMoments.length === 0 ? (
        <EmptyState
          glyph={<IconGlobe />}
          title={t("explore.empty")}
          hint={t("explore.emptyHint")}
        />
      ) : (
        <div className="pub-feed">
          {exploreMoments.map((entry) => (
            <MomentRow
              key={entry.id}
              entry={entry}
              onOpenAuthor={(row) => setSheet({ kind: "author", user: row.author })}
              onOpenMoment={openOnMap}
              onOpenDetail={(row) => setSheet({ kind: "entry", entry: row })}
            />
          ))}
        </div>
      )}

      <Sheet
        open={Boolean(sheet)}
        title={
          sheet?.kind === "author"
            ? sheet.user.displayName
            : sheet?.kind === "entry"
              ? sheet.entry.placeName || t("explore.noPlace")
              : t("nav.timeline")
        }
        onClose={() => setSheet(null)}
      >
        {sheet?.kind === "author" ? (
          <TravellerTimeline
            user={sheet.user}
            onOpenMoment={(entry) => setSheet({ kind: "entry", entry })}
          />
        ) : null}
        {sheet?.kind === "entry" ? (
          <MomentDetail
            entry={sheet.entry}
            onOpenTimeline={(user) => setSheet({ kind: "author", user })}
          />
        ) : null}
      </Sheet>
    </div>
  );
}
